/* ═══════════════════════════════════════════════════════════════════════════
   v3.2 — DASHBOARD controllers: RAG, cadence, products, CRM link, milestones,
   key risks, success projects + the action-assignment picker.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Shared date helpers for the milestone list ──────────────────────────── */
/* Parse an action due date (stored as 'YYYY-MM-DD' or '' / null) to a Date at
   local midnight, or null if absent/unparseable. */
function _parseDueDate(s) {
  if (!s || typeof s !== 'string') return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? null : d;
}
const _MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
/* Compact display date: "20 May 2026". */
function _fmtCompactDate(d) {
  return `${d.getDate()} ${_MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}
/* Relative-time string from today to a due date: "in 5 weeks" / "7 days overdue"
   / "today". Uses whole-day difference at local midnight. */
function _relTime(due) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(due); d.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / 86400000);
  if (days === 0) return 'today';
  if (days < 0) {
    const n = -days;
    return n === 1 ? '1 day overdue' : `${n} days overdue`;
  }
  if (days <= 14) return days === 1 ? 'in 1 day' : `in ${days} days`;
  const weeks = Math.round(days / 7);
  return `in ${weeks} weeks`;
}

/* ── Project Status: RAG pill (click-to-cycle) + rationale ───────────────── */
const _RAG_CYCLE = [null, 'red', 'amber', 'green'];   /* Not set → R → A → G → … */
const _RAG_LABEL = { null: 'Not set', red: 'Red', amber: 'Amber', green: 'Green' };
function _ragClassFor(level) { return level === 'red' ? 'rag-red' : level === 'amber' ? 'rag-amber' : level === 'green' ? 'rag-green' : 'rag-none'; }
function renderRag() {
  const pill = document.getElementById('ragPill');
  if (!pill) return;
  const level = appMeta.dashboard.rag.level;
  pill.classList.remove('rag-red', 'rag-amber', 'rag-green', 'rag-none');
  pill.classList.add(_ragClassFor(level));
  pill.textContent = (level === null) ? 'Not set' : _RAG_LABEL[level];
  const rat = document.getElementById('ragRationale');
  if (rat && rat.textContent !== (appMeta.dashboard.rag.rationale || '')) {
    rat.textContent = appMeta.dashboard.rag.rationale || '';
  }
  renderOverallProgress();
}

/* ══ v17.5 P7: ONE PROJECT PROGRESS VALUE + AN EXPLICIT ASSESSMENT DRAFT ═══════════════════
   The automatic calculation remains the underlying gauge. A present progressAssessment object
   chooses the manager's reported value; absence chooses the gauge. Rendering and help are pure.
   The editor is the sole writer and keeps every keystroke outside the model until Apply. */
function _progressActiveProject() {
  const db = window.__DB__;
  return (db && Array.isArray(db.projects))
    ? db.projects.find(function (c) { return c && c.id === activeProjectId; }) || null : null;
}
function _progressAssessmentHolder(c) {
  const d = c && c.appMeta && c.appMeta.dashboard;
  return (d && typeof d === 'object' && !Array.isArray(d)) ? d : null;
}
function _progressHasAssessment(c) {
  const d = _progressAssessmentHolder(c);
  return !!(d && Object.prototype.hasOwnProperty.call(d, 'progressAssessment'));
}
function _progressIsoValid(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)
    && Number.isFinite(new Date(value).getTime());
}
function _progressProjectEditable(c) {
  if (!_progressControllerReady || !c || c.archived || c.id !== activeProjectId
      || c.appMeta !== appMeta) return false;
  try {
    return _dbLoadOk && !_fileAlertBlocked();
  } catch (e) { return false; }
}
function _progressValueText(value) { return value === null ? '—' : String(value) + '%'; }
function _progressHelpButtonHtml(c, extraClass) {
  if (!_progressControllerReady || !c || typeof c.uid !== 'string' || !c.uid) return '';
  return `<button type="button" class="ov-info-i progress-help-trigger${extraClass ? ' ' + extraClass : ''}"`
    + ` data-progress-help-kind="project" data-project-uid="${_escapeHtml(c.uid)}"`
    + ` aria-label="About overall progress" aria-expanded="false">i</button>`;
}
function renderOverallProgress() {
  const host = document.getElementById('projectProgressSummary');
  if (!host) return;
  if (!_progressControllerReady) { host.innerHTML = ''; return; }
  const c = _progressActiveProject();
  if (!c) { host.innerHTML = ''; return; }
  const result = _overallProjectProgress(c);
  const value = _progressValueText(result.percent);
  const width = result.percent === null ? 0 : result.percent;
  const editable = _progressProjectEditable(c);
  const valueHtml = editable
    ? `<button type="button" class="project-progress-value" data-project-progress-edit="${_escapeHtml(c.uid)}"`
      + ` aria-label="Edit overall progress" onclick="openProjectProgressEditor(this)">${value}</button>`
    : `<span class="project-progress-value">${value}</span>`;
  host.innerHTML = `<div class="project-progress-heading"><span>Overall progress</span>`
    + _progressHelpButtonHtml(c)
    + `<span class="project-progress-source">${result.source === 'assessment' ? 'My assessment' : 'Based on actions'}</span></div>`
    + `<span class="project-progress-track" aria-hidden="true"><span class="project-progress-fill" style="width:${width}%"></span></span>`
    + `<span class="project-progress-value-wrap">${valueHtml}`
    + (result.source === 'assessment' ? `<span class="progress-assessed">Assessed</span>` : '')
    + `</span>`;
}

function _progressSavedDraft(c) {
  const result = _overallProjectProgress(c);
  const holder = _progressAssessmentHolder(c);
  const saved = holder && holder.progressAssessment;
  return {
    source: result.source,
    percent: result.source === 'assessment' && !result.invalidAssessment ? String(result.percent) : '',
    note: result.source === 'assessment' && saved && typeof saved.note === 'string' ? saved.note : ''
  };
}
function _progressDraftKey(draft) {
  return JSON.stringify({ source:draft.source, percent:draft.percent, note:draft.note });
}
function _progressReadDraft() {
  if (!_projectProgressEditor) return null;
  const chosen = document.querySelector('input[name="projectProgressSource"]:checked');
  const pct = document.getElementById('projectProgressPercent');
  const note = document.getElementById('projectProgressNote');
  const draft = {
    source: chosen && chosen.value === 'assessment' ? 'assessment' : 'actions',
    percent: pct ? pct.value : '',
    note: note ? note.value : ''
  };
  _projectProgressEditor.draft = draft;
  return draft;
}
function _projectProgressDialogOpen() {
  const dialog = document.getElementById('projectProgressDialog');
  return !!(_projectProgressEditor && dialog && dialog.open);
}
function _progressDraftChanged() {
  if (!_projectProgressEditor) return false;
  const draft = _projectProgressDialogOpen() ? _progressReadDraft() : _projectProgressEditor.draft;
  return _progressDraftKey(draft) !== _progressDraftKey(_projectProgressEditor.initial);
}
function _progressClearErrors() {
  ['projectProgressPercentError','projectProgressNoteError','projectProgressDialogError'].forEach(function (id) {
    const el = document.getElementById(id); if (el) el.hidden = true;
  });
  ['projectProgressPercent','projectProgressNote'].forEach(function (id) {
    const el = document.getElementById(id); if (el) el.removeAttribute('aria-invalid');
  });
}
function _progressSyncSourceUi(focusPercent) {
  const chosen = document.querySelector('input[name="projectProgressSource"]:checked');
  const assessment = !!(chosen && chosen.value === 'assessment');
  const fields = document.getElementById('projectProgressAssessmentFields');
  const auto = document.getElementById('projectProgressAutoCopy');
  if (fields) fields.hidden = !assessment;
  if (auto) auto.hidden = assessment;
  _progressReadDraft();
  if (assessment && focusPercent) {
    requestAnimationFrame(function () {
      const input = document.getElementById('projectProgressPercent');
      if (input) input.focus({ preventScroll:true });
    });
  }
}
function _progressFocusDialog() {
  const dialog = document.getElementById('projectProgressDialog');
  if (!dialog || !dialog.open) return;
  const invalid = dialog.querySelector('[aria-invalid="true"]');
  const selected = dialog.querySelector('input[name="projectProgressSource"]:checked');
  const target = invalid || selected || dialog;
  if (target && typeof target.focus === 'function') target.focus({ preventScroll:true });
}
function _closeProjectProgressEditor(returnFocus) {
  const state = _projectProgressEditor;
  const dialog = document.getElementById('projectProgressDialog');
  _projectProgressEditor = null;
  _projectProgressApplying = false;
  _progressClearErrors();
  if (dialog && dialog.open) dialog.close();
  const pct = document.getElementById('projectProgressPercent');
  const note = document.getElementById('projectProgressNote');
  if (pct) pct.value = '';
  if (note) note.value = '';
  if (returnFocus && state) {
    requestAnimationFrame(function () {
      let target = state.opener && state.opener.isConnected ? state.opener : null;
      if (!target && state.uid) target = document.querySelector(`[data-project-progress-edit="${CSS.escape(state.uid)}"]`);
      if (target && typeof target.focus === 'function') target.focus({ preventScroll:true });
    });
  }
}
function cancelProjectProgressEditor() { _closeProjectProgressEditor(true); }
function openProjectProgressEditor(opener) {
  if (_projectProgressDialogOpen()) return;
  const c = _progressActiveProject();
  if (!c || !_progressProjectEditable(c) || typeof c.uid !== 'string' || !c.uid) return;
  _progressHelpClose();
  const initial = _progressSavedDraft(c);
  _projectProgressEditor = {
    uid:c.uid, projectId:c.id, initial:{...initial}, draft:{...initial}, opener:opener || null,
    route:location.hash || ('#/project/' + encodeURIComponent(c.id)), tab:activeTab
  };
  const dialog = document.getElementById('projectProgressDialog');
  const name = document.getElementById('projectProgressProjectName');
  const actions = document.getElementById('projectProgressSourceActions');
  const assessment = document.getElementById('projectProgressSourceAssessment');
  const pct = document.getElementById('projectProgressPercent');
  const note = document.getElementById('projectProgressNote');
  if (!dialog || !actions || !assessment || !pct || !note) { _projectProgressEditor = null; return; }
  if (name) name.textContent = c.name || 'Untitled project';
  actions.checked = initial.source === 'actions';
  assessment.checked = initial.source === 'assessment';
  pct.value = initial.percent;
  note.value = initial.note;
  _progressClearErrors();
  _progressSyncSourceUi(false);
  dialog.showModal();
  requestAnimationFrame(function () {
    const selected = dialog.querySelector('input[name="projectProgressSource"]:checked');
    if (selected) selected.focus({ preventScroll:true });
  });
}
function _progressShowStaleEditorError() {
  const error = document.getElementById('projectProgressDialogError');
  if (error) {
    error.textContent = 'This project is no longer editable. Copy your assessment note before closing.';
    error.hidden = false;
  }
  _progressFocusDialog();
}
function _progressAssessmentUnchanged(saved, percent, note) {
  return !!(saved && typeof saved === 'object' && !Array.isArray(saved)
    && Number.isInteger(saved.percent) && saved.percent >= 0 && saved.percent <= 100
    && typeof saved.note === 'string'
    && saved.percent === percent && saved.note === note);
}
function applyProjectProgressEditor(ev) {
  if (ev) ev.preventDefault();
  if (!_projectProgressDialogOpen() || _projectProgressApplying) return false;
  _projectProgressApplying = true;
  _progressClearErrors();
  const state = _projectProgressEditor;
  const draft = _progressReadDraft();
  let percent = null;
  let note = String(draft.note || '').replace(/\r\n?/g, '\n').trim();
  if (draft.source === 'assessment') {
    const raw = String(draft.percent || '').trim();
    percent = /^\d+$/.test(raw) ? Number(raw) : NaN;
    const pctInvalid = !Number.isInteger(percent) || percent < 0 || percent > 100;
    const noteInvalid = note.length > 500;
    if (pctInvalid || noteInvalid) {
      const pctError = document.getElementById('projectProgressPercentError');
      const noteError = document.getElementById('projectProgressNoteError');
      const pctInput = document.getElementById('projectProgressPercent');
      const noteInput = document.getElementById('projectProgressNote');
      if (pctError) pctError.hidden = !pctInvalid;
      if (noteError) noteError.hidden = !noteInvalid;
      if (pctInput) pctInput.setAttribute('aria-invalid', pctInvalid ? 'true' : 'false');
      if (noteInput) noteInput.setAttribute('aria-invalid', noteInvalid ? 'true' : 'false');
      (pctInvalid ? pctInput : noteInput).focus({ preventScroll:true });
      _projectProgressApplying = false;
      return false;
    }
  }
  const c = _projectByUid(state.uid);
  if (!c || c.id !== state.projectId || !_progressProjectEditable(c)) {
    _projectProgressApplying = false;
    _progressShowStaleEditorError();
    return false;
  }
  const holder = _progressAssessmentHolder(c);
  const has = !!(holder && Object.prototype.hasOwnProperty.call(holder, 'progressAssessment'));
  let changed = false;
  if (draft.source === 'actions') {
    if (has) { delete holder.progressAssessment; changed = true; }
  } else {
    const saved = has ? holder.progressAssessment : null;
    if (!_progressAssessmentUnchanged(saved, percent, note)) {
      holder.progressAssessment = { percent:percent, note:note, assessedAt:new Date().toISOString() };
      changed = true;
    }
  }
  if (changed) markDirty();
  renderOverallProgress();
  _closeProjectProgressEditor(true);
  return true;
}

function _progressBlockSaveOrExport() {
  if (!_projectProgressDialogOpen()) return false;
  showToast('Apply or cancel the progress change before saving or exporting.', { type:'info', duration:5200 });
  _progressFocusDialog();
  return true;
}
function _progressBlockNavigation() {
  const state = _projectProgressEditor;
  if (!state || !_projectProgressDialogOpen()) return false;
  if (!_progressDraftChanged()) { _closeProjectProgressEditor(false); return false; }
  if (state.route && location.hash !== state.route) {
    try { history.replaceState(null, '', state.route); } catch (e) {}
  }
  showToast('Apply or cancel the progress change before leaving this project.', { type:'info', duration:5200 });
  _progressFocusDialog();
  return true;
}
function _progressPermitRoute(targetHash) {
  if (!_projectProgressDialogOpen()) return true;
  const target = targetHash || '#/portfolio';
  if (_projectProgressEditor.route === target) return true;
  return !_progressBlockNavigation();
}
function _progressPermitTab(targetTab) {
  if (!_projectProgressDialogOpen() || targetTab === _projectProgressEditor.tab) return true;
  return !_progressBlockNavigation();
}
function _progressPermitProject(targetId) {
  if (!_projectProgressDialogOpen() || targetId === _projectProgressEditor.projectId) return true;
  return !_progressBlockNavigation();
}

function _progressHelpProjectContent(trigger) {
  const c = _projectByUid(trigger.dataset.projectUid || '');
  if (!c) return null;
  const result = _overallProjectProgress(c);
  const lines = [];
  lines.push({ text:'Source: ' + (result.source === 'assessment' ? 'My assessment' : 'Based on actions'), strong:true });
  lines.push({ text:result.actionCount
    ? `Action progress: ${result.actionPercent}% from ${result.actionCount} linked action${result.actionCount === 1 ? '' : 's'}.`
    : 'Action progress: —. No linked actions to include.' });
  lines.push({ text:'Each action counts equally. Completed actions count as 100%; cancelled actions are excluded. Missing progress counts as 0%. Unlinked actions and work not captured as actions are excluded.' });
  if (result.source === 'assessment') {
    let when = 'unavailable';
    if (_progressIsoValid(result.assessedAt)) {
      when = new Date(result.assessedAt).toLocaleString('en-GB', {
        day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit'
      });
    }
    lines.push({ text:'Last assessed: ' + when + (when === 'unavailable' ? '.' : ' (local time).') });
    if (typeof result.note === 'string' && result.note.trim()) {
      lines.push({ label:'Assessment note:', text:result.note.replace(/\r\n?/g, '\n').trim(), note:true });
    }
    if (result.invalidAssessment) {
      lines.push({ text:'The saved assessment is invalid. Edit progress to correct it or choose Based on actions.' });
    }
  }
  return lines;
}
function _progressHelpWorkstreamContent(trigger) {
  const c = _projectByUid(trigger.dataset.projectUid || '');
  if (!c || c.appMeta !== appMeta || !Array.isArray(c.workstreams)) return null;
  const id = Number(trigger.dataset.workstreamId);
  if (!c.workstreams.some(function (w) { return w && w.id === id; })) return null;
  return [{ text:'Average progress of actions in this workstream and its sub-items. Each action counts equally. Completed actions count as 100%; cancelled actions are excluded. Missing progress counts as 0%. A dash means there are no actions to include. The status pill is set separately.' }];
}
function _progressHelpContent(trigger) {
  if (!trigger) return null;
  if (trigger.dataset.progressHelpKind === 'project') return _progressHelpProjectContent(trigger);
  if (trigger.dataset.progressHelpKind === 'workstream') return _progressHelpWorkstreamContent(trigger);
  return null;
}
function _progressHelpPosition() {
  const tooltip = document.getElementById('progressHelpTooltip');
  const trigger = _progressHelpTrigger;
  if (!tooltip || tooltip.hidden || !trigger || !trigger.isConnected) { _progressHelpClose(); return; }
  const r = trigger.getBoundingClientRect();
  const gap = 7, edge = 12;
  const box = tooltip.getBoundingClientRect();
  let left = Math.min(Math.max(edge, r.left + r.width / 2 - box.width / 2), innerWidth - box.width - edge);
  let top = r.bottom + gap;
  if (top + box.height > innerHeight - edge) top = Math.max(edge, r.top - box.height - gap);
  tooltip.style.left = Math.round(left) + 'px';
  tooltip.style.top = Math.round(top) + 'px';
}
function _progressHelpOpen(trigger, pinned) {
  if (!pinned && _progressHelpDismissedTrigger === trigger) return;
  _progressHelpDismissedTrigger = null;
  const tooltip = document.getElementById('progressHelpTooltip');
  const lines = _progressHelpContent(trigger);
  if (!tooltip || !lines) { _progressHelpClose(); return; }
  if (_progressHelpHideTimer) { clearTimeout(_progressHelpHideTimer); _progressHelpHideTimer = null; }
  if (_progressHelpTrigger && _progressHelpTrigger !== trigger) {
    _progressHelpTrigger.setAttribute('aria-expanded', 'false');
    _progressHelpTrigger.removeAttribute('aria-describedby');
  }
  _progressHelpTrigger = trigger;
  if (typeof pinned === 'boolean') _progressHelpPinned = pinned;
  tooltip.innerHTML = '';
  lines.forEach(function (line) {
    const p = document.createElement('p'); p.className = 'progress-help-line';
    if (line.label) {
      const b = document.createElement('strong'); b.textContent = line.label; p.appendChild(b);
      const body = document.createElement('div'); body.className = 'progress-help-note'; body.textContent = line.text; p.appendChild(body);
    } else {
      p.textContent = line.text;
      if (line.strong) p.style.fontWeight = '650';
    }
    tooltip.appendChild(p);
  });
  tooltip.hidden = false;
  trigger.setAttribute('aria-expanded', 'true');
  trigger.setAttribute('aria-describedby', 'progressHelpTooltip');
  _progressHelpPosition();
}
function _progressHelpClose() {
  _progressHelpDismissedTrigger = null;
  if (_progressHelpHideTimer) { clearTimeout(_progressHelpHideTimer); _progressHelpHideTimer = null; }
  const tooltip = document.getElementById('progressHelpTooltip');
  if (tooltip) { tooltip.hidden = true; tooltip.innerHTML = ''; }
  if (_progressHelpTrigger) {
    _progressHelpTrigger.setAttribute('aria-expanded', 'false');
    _progressHelpTrigger.removeAttribute('aria-describedby');
  }
  _progressHelpTrigger = null;
  _progressHelpPinned = false;
}
function _progressHelpDismiss() {
  const prior = _progressHelpTrigger || _progressHelpDismissedTrigger;
  _progressHelpClose();
  _progressHelpDismissedTrigger = prior;
}
function _progressHelpScheduleClose() {
  if (_progressHelpPinned) return;
  if (_progressHelpHideTimer) clearTimeout(_progressHelpHideTimer);
  _progressHelpHideTimer = setTimeout(function () {
    _progressHelpHideTimer = null;
    const trigger = _progressHelpTrigger;
    const tooltip = document.getElementById('progressHelpTooltip');
    if (_progressHelpPinned || (trigger && (document.activeElement === trigger || trigger.matches(':hover')))
        || (tooltip && !tooltip.hidden && tooltip.matches(':hover'))) return;
    _progressHelpClose();
  }, 100);
}
function _initProjectProgressController() {
  if (_progressHelpBound) return;
  _progressHelpBound = true;
  const dialog = document.getElementById('projectProgressDialog');
  const form = document.getElementById('projectProgressForm');
  const cancel = document.getElementById('projectProgressCancel');
  const pct = document.getElementById('projectProgressPercent');
  const note = document.getElementById('projectProgressNote');
  if (form) form.addEventListener('submit', applyProjectProgressEditor);
  if (cancel) cancel.addEventListener('click', cancelProjectProgressEditor);
  document.querySelectorAll('input[name="projectProgressSource"]').forEach(function (radio) {
    radio.addEventListener('change', function () { _progressClearErrors(); _progressSyncSourceUi(radio.value === 'assessment'); });
  });
  [pct,note].forEach(function (field) { if (field) field.addEventListener('input', _progressReadDraft); });
  if (pct) pct.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (!e.isComposing && !e.repeat) applyProjectProgressEditor(e);
  });
  if (dialog) dialog.addEventListener('cancel', function (e) { e.preventDefault(); cancelProjectProgressEditor(); });

  document.addEventListener('click', function (e) {
    const trigger = e.target.closest && e.target.closest('.progress-help-trigger');
    const tooltip = document.getElementById('progressHelpTooltip');
    if (trigger) {
      e.preventDefault(); e.stopPropagation();
      if (_progressHelpTrigger === trigger && tooltip && !tooltip.hidden && _progressHelpPinned) _progressHelpDismiss();
      else _progressHelpOpen(trigger, true);
      return;
    }
    if (tooltip && tooltip.contains(e.target)) { e.stopPropagation(); return; }
    _progressHelpDismiss();
  }, true);
  document.addEventListener('keydown', function (e) {
    const trigger = e.target.closest && e.target.closest('.progress-help-trigger');
    if (trigger && (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar')) e.stopPropagation();
    if (e.key === 'Escape' && _progressHelpTrigger) {
      e.preventDefault(); e.stopPropagation();
      const prior = _progressHelpTrigger; _progressHelpDismiss();
      if (prior && prior.isConnected) prior.focus({ preventScroll:true });
    }
  }, true);
  document.addEventListener('pointerover', function (e) {
    const trigger = e.target.closest && e.target.closest('.progress-help-trigger');
    if (trigger && (!_progressHelpPinned || _progressHelpTrigger !== trigger)) _progressHelpOpen(trigger, false);
    else if (e.target.closest && e.target.closest('#progressHelpTooltip') && _progressHelpHideTimer) {
      clearTimeout(_progressHelpHideTimer); _progressHelpHideTimer = null;
    }
  }, true);
  document.addEventListener('pointerout', function (e) {
    const from = e.target.closest && e.target.closest('.progress-help-trigger,#progressHelpTooltip');
    const to = e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest('.progress-help-trigger,#progressHelpTooltip');
    if (from && from !== to) {
      if (_progressHelpDismissedTrigger === from) _progressHelpDismissedTrigger = null;
      _progressHelpScheduleClose();
    }
  }, true);
  document.addEventListener('focusin', function (e) {
    const trigger = e.target.closest && e.target.closest('.progress-help-trigger');
    if (trigger && (!_progressHelpPinned || _progressHelpTrigger !== trigger)) _progressHelpOpen(trigger, false);
  }, true);
  document.addEventListener('focusout', function (e) {
    const trigger = e.target.closest && e.target.closest('.progress-help-trigger');
    if (trigger) {
      if (_progressHelpDismissedTrigger === trigger) _progressHelpDismissedTrigger = null;
      if (e.relatedTarget !== document.getElementById('progressHelpTooltip')) _progressHelpScheduleClose();
    }
  }, true);
  window.addEventListener('resize', _progressHelpPosition);
  window.addEventListener('scroll', _progressHelpClose, true);
  if (typeof MutationObserver === 'function') {
    new MutationObserver(function () {
      if ((_progressHelpTrigger && !_progressHelpTrigger.isConnected)
          || (_progressHelpDismissedTrigger && !_progressHelpDismissedTrigger.isConnected)) _progressHelpClose();
    }).observe(document.body, { childList:true, subtree:true });
  }
}
function cycleRag() {
  const cur = appMeta.dashboard.rag.level;
  let i = _RAG_CYCLE.indexOf(cur);
  if (i < 0) i = 0;
  appMeta.dashboard.rag.level = _RAG_CYCLE[(i + 1) % _RAG_CYCLE.length];
  markDirty();
  renderRag();
}

/* v8 Phase 2: Key products + CRM link controllers removed (project-success fields).
   v10.2: the meeting-cadence binder (_bindCadence) removed with its dormant data —
   the comms plan has owned cadence since v8.6 (D5). */

/* ══ v8 Phase 4.0: PgMP dashboard sections ══════════════════════════════════ */

/* ── Scope statement (five lists) ──────────────────────────────────────────── */
const _SCOPE_FIELDS = [
  ['inScope', 'In scope'], ['outOfScope', 'Out of scope'],
  ['acceptance', 'Acceptance criteria'], ['constraints', 'Constraints'], ['assumptions', 'Assumptions']
];
function renderScope() {
  const wrap = document.getElementById('scopeGrid');
  if (!wrap) return;
  const sc = appMeta.dashboard.scope;
  const project = _scopeActiveProject();
  const projectUid = project && typeof project.uid === 'string' ? project.uid : '';
  wrap.innerHTML = _SCOPE_FIELDS.map(([key, label]) => {
    const items = (sc[key] || []).map((txt, i) =>
      `<li class="scope-item"><span class="scope-item-txt" data-scope-key="${key}" data-scope-index="${i}" contenteditable="true" spellcheck="false" onkeydown="scopeItemKeydown(event,this)" onblur="editScopeItem('${key}',${i},this.textContent)">${_escapeHtml(txt)}</span><button class="scope-del" title="Remove" onclick="removeScopeItem('${key}',${i})">&times;</button></li>`
    ).join('');
    /* \u26a0 v15.1: the add box commits on BLUR as well as Enter \u2014 his report, reproduced against a
       copy of his real file: he typed into "+ add\u2026" and clicked Save (or clicked away), and the
       text was neither saved nor marked dirty, because Enter was the ONLY commit and nothing \u2014
       not even the save-time DOM sweep \u2014 read these inputs. His scope lists were EMPTY on disk
       after he had "edited the scope statement". The action-card LABEL adder solved this exact
       class long ago with the same onblur; the reference-typed adders (#5 chips) stay
       Enter-only on purpose, because blur-committing a half-typed reference fires refusal
       toasts on click-away. \u26a0 The onblur also makes A12's delegated input listener see this as
       a blur-committing editor, so TYPING in it now arms the dirty flag and the leave-site
       prompt \u2014 the two halves of "the indicator does not show either". */
    return `<div class="scope-block"><div class="scope-lbl">${label}</div><ul class="scope-list">${items}</ul><input class="scope-add" type="text" data-scope-key="${key}" data-scope-project-uid="${_escapeHtml(projectUid)}" aria-label="Add to ${label}" placeholder="+ add\u2026" onkeydown="scopeAddKeydown(event,this,'${key}')" onblur="commitScopeAdd(this,'${key}','blur',event)"></div>`;
  }).join('');
}
function _scopeFieldAllowed(key) {
  return _SCOPE_FIELDS.some(row => row[0] === key);
}
function _scopeActiveProject() {
  const db = window.__DB__;
  return db && Array.isArray(db.projects)
    ? db.projects.find(c => c && c.appMeta === appMeta) : null;
}
function _restoreScopeFocus(target) {
  if (!target) return;
  const selector = target.kind === 'add'
    ? `.scope-add[data-scope-key="${CSS.escape(target.key)}"]`
    : `.scope-item-txt[data-scope-key="${CSS.escape(target.key)}"][data-scope-index="${target.index}"]`;
  requestAnimationFrame(() => {
    const project = _scopeActiveProject();
    if (!project || project.uid !== target.projectUid) return;
    const next = document.querySelector(selector);
    if (next && typeof next.focus === 'function') next.focus({ preventScroll: true });
  });
}
function commitScopeAdd(input, key, cause, ev) {
  if (!input || !input.isConnected || input.dataset.scopeConsumed === '1' || !_scopeFieldAllowed(key)) return false;
  const project = _scopeActiveProject();
  if (!project || typeof project.uid !== 'string' || input.dataset.scopeProjectUid !== project.uid) return false;
  if (input.disabled || input.readOnly) return false;

  let restore = null;
  const destination = ev && ev.relatedTarget;
  if (cause === 'enter') restore = { kind: 'add', key, projectUid: project.uid };
  else if (destination && document.getElementById('scopeGrid').contains(destination)) {
    if (destination.classList.contains('scope-add')) {
      restore = { kind: 'add', key: destination.dataset.scopeKey || '', projectUid: project.uid };
    } else if (destination.classList.contains('scope-item-txt')) {
      restore = { kind: 'item', key: destination.dataset.scopeKey || '', index: destination.dataset.scopeIndex || '0', projectUid: project.uid };
    }
  }

  const raw = input.value || '';
  if (raw === '') return true;
  input.dataset.scopeConsumed = '1';
  const txt = raw.trim();
  input.value = '';
  if (!txt) {
    if (cause === 'enter') {
      renderScope();
      _restoreScopeFocus(restore);
    } else {
      delete input.dataset.scopeConsumed;
    }
    return true;
  }
  const arr = appMeta.dashboard.scope[key];
  if (!Array.isArray(arr)) return false;
  arr.push(txt);
  markDirty();
  renderScope();
  _restoreScopeFocus(restore);
  return true;
}
function scopeAddKeydown(ev, input, key) {
  if (!ev || ev.key !== 'Enter' || ev.isComposing || ev.repeat) return;
  if (commitScopeAdd(input, key, 'enter', ev)) ev.preventDefault();
}
function scopeItemKeydown(ev, input) {
  if (!ev || ev.key !== 'Enter' || ev.isComposing) return;
  ev.preventDefault();
  if (!ev.repeat && input && typeof input.blur === 'function') input.blur();
}
function addScopeItem(key, txt) {
  txt = (txt || '').trim();
  const project = _scopeActiveProject();
  if (!project || !_scopeFieldAllowed(key) || !txt) return false;
  const arr = appMeta.dashboard.scope[key];
  if (!Array.isArray(arr)) return false;
  arr.push(txt); markDirty(); renderScope(); return true;
}
function editScopeItem(key, i, txt) {
  const arr = appMeta.dashboard.scope[key]; if (!arr || i < 0 || i >= arr.length) return;
  txt = (txt || '').trim();
  if (txt === '') { arr.splice(i, 1); markDirty(); renderScope(); return; }  // v8.2.2 (Q4): clearing a bullet removes it (the × stays the primary remove)
  if (arr[i] !== txt) { arr[i] = txt; markDirty(); }
}
function removeScopeItem(key, i) {
  const arr = appMeta.dashboard.scope[key];
  if (arr && i >= 0 && i < arr.length) { arr.splice(i, 1); markDirty(); renderScope(); }
}

/* ── Stage gates ───────────────────────────────────────────────────────────── */
function renderGates() {
  const strip = document.getElementById('gateStrip');
  if (!strip) return;
  const g = appMeta.dashboard.gates;
  strip.innerHTML = g.names.map((name, i) => {
    let cls = 'upcoming', status = 'Upcoming';
    if (g.current >= 0) {
      if (i < g.current) { cls = 'passed'; status = '\u2713 Passed'; }
      else if (i === g.current) { cls = 'current'; status = '\u25CF Current'; }
    }
    return `<div class="gate ${cls}" onclick="setGate(${i},event)"><div class="gate-n">G${i}</div><div class="gate-name" contenteditable="true" spellcheck="false" onclick="event.stopPropagation()" onblur="editGateName(${i},this.textContent)">${_escapeHtml(name)}</div><div class="gate-s">${status}</div></div>`;
  }).join('');
}
function setGate(i, ev) {
  if (ev && ev.target && ev.target.classList && ev.target.classList.contains('gate-name')) return;
  appMeta.dashboard.gates.current = (appMeta.dashboard.gates.current === i) ? -1 : i;
  markDirty(); renderGates();
}
function editGateName(i, txt) {
  txt = (txt || '').trim();
  const names = appMeta.dashboard.gates.names;
  if (i >= 0 && i < names.length && names[i] !== txt) { names[i] = txt; markDirty(); }
}

/* v10.2: the dormant Schedule & budget machinery (bindSchedule · _updateScheduleHint ·
   renderBudget · cycleBudgetRag) removed with its data — schedule lives on
   Scope ▸ Schedule (v9.0); budget figures live outside the tool. Q6's schedule hint
   retires with it; its idiom lives on in the WBS date hint. */

/* ── Benefits — status vocabulary only. SHARED: the v10.0 programme benefits
   register reads _BEN_STATUS/_benStatusMeta. The project-level Benefits CRUD
   (renderBenefits and friends) was removed in v10.2 with its dormant data —
   benefits are programme-level (v10.0). ── */
const _BEN_STATUS = [
  [null, 'Not set', 'b-none'], ['ontrack', 'On track', 'b-green'],   /* v10.0: null label 'Set status' → 'Not set' (§v10.0 wording) */
  ['atrisk', 'At risk', 'b-amber'], ['offtrack', 'Off track', 'b-red'], ['realised', 'Realised', 'b-blue']
];
function _benStatusMeta(s) { return _BEN_STATUS.find(x => x[0] === s) || _BEN_STATUS[0]; }
/* ══ end Phase 4.0 dashboard sections ═══════════════════════════════════════ */

/* ── v8 Phase 4.0: Change-request register (Changes tab) ───────────────────── */
const _CR_TYPE = [['scope', 'Scope'], ['schedule', 'Schedule'], ['budget', 'Budget'], ['quality', 'Quality'], ['other', 'Other']];
const _CR_IMPACT = [[null, 'Impact?', 'ci-none'], ['low', 'Low', 'ci-low'], ['medium', 'Medium', 'ci-med'], ['high', 'High', 'ci-high']];
const _CR_STATUS = [
  ['submitted', 'Submitted', 'cs-submitted'], ['review', 'Under review', 'cs-review'],
  ['approved', 'Approved', 'cs-approved'], ['rejected', 'Rejected', 'cs-rejected'],
  ['deferred', 'Deferred', 'cs-deferred'], ['implemented', 'Implemented', 'cs-implemented']
];
function _crMeta(arr, v) { return arr.find(x => x[0] === v) || arr[0]; }
function _crPad(n) {
  /* v14 (W3b): the signature stays a NUMBER because several callers pass c.num, so the
     record is resolved here and its DISPLAY ordinal derived. */
  const _cr = (appMeta && Array.isArray(appMeta.changeRequests)) ? appMeta.changeRequests : [];
  const _rec = _cr.filter(function (c) { return c && !c.del; })
                  .find(function (c) { return String(c.num) === String(n); });
  return 'CR-' + String(_rec ? _mintDisplayOrd(_cr, _rec) : n).padStart(3, '0');
}
function renderChangeRequests() {
  const wrap = document.getElementById('crWrap'); if (!wrap) return;
  const crs = appMeta.changeRequests;
  if (!crs.length) { wrap.innerHTML = `<div class="cr-empty">No change requests logged. Click \u201cLog change request\u201d to raise one.</div>`; return; }
  /* v10.2 (Q10-partial): handlers are keyed by the stable CR number (c.num), not the
     array index \u2014 the register already carried a never-reused per-project number. */
  wrap.innerHTML = crs.map(c => {
    const ty = _crMeta(_CR_TYPE, c.type), im = _crMeta(_CR_IMPACT, c.impact), st = _crMeta(_CR_STATUS, c.status);
    return `<div class="cr-card"><div class="cr-top"><span class="cr-ref">${_crPad(c.num)}</span><input type="date" class="cr-date" value="${_escapeHtml(c.date)}" onchange="editCR(${c.num},'date',this.value)"><span class="cr-type" onclick="cycleCR(${c.num},'type')" title="Change type">${ty[1]}</span><span class="cr-spacer"></span><span class="cr-status ${st[2]}" onclick="cycleCR(${c.num},'status')" title="Workflow status">${st[1]}</span><button class="cr-del" title="Remove" onclick="removeChangeRequest(${c.num})">&times;</button></div><div class="cr-desc" contenteditable="true" spellcheck="false" data-ph="Describe the requested change\u2026" onblur="editCR(${c.num},'description',this.textContent)">${_escapeHtml(c.description)}</div><div class="cr-foot"><span class="cr-foot-lbl">Requested by</span><span class="cr-req" contenteditable="true" spellcheck="false" data-ph="name\u2026" onblur="editCR(${c.num},'requester',this.textContent)">${_escapeHtml(c.requester)}</span><span class="cr-foot-lbl">Impact</span><span class="cr-impact ${im[2]}" onclick="cycleCR(${c.num},'impact')">${im[1]}</span></div></div>`;
  }).join('');
}
function addChangeRequest() {
  /* v8.2.1 (Q2): monotonic per-project CR numbers — a number is never reused, even
     after the highest-numbered CR is deleted. appMeta.crSeq is a high-water mark that
     only ever rises and rides along in the wholesale-serialised appMeta (no serialiser
     change). v14 (W3b): the number now comes from _mintNextId, which self-heals from the
     records themselves and honours the stored crSeq only inside THIS writer's block. */
  const num = _mintNextId(appMeta.changeRequests, _myWriterId(), appMeta.crSeq);   /* v14 (W3b) */
  appMeta.crSeq = num;
  const today = _todayISO();
  appMeta.changeRequests.push({ num, date: today, description: '', requester: '', type: 'scope', impact: null, status: 'submitted' });
  markDirty(); renderChangeRequests();
}
function editCR(num, field, val) {
  const c = appMeta.changeRequests.find(x => x.num === num); if (!c) return;
  val = (val || '').trim();
  if (c[field] !== val) { c[field] = val; markDirty(); }
}
function cycleCR(num, field) {
  const c = appMeta.changeRequests.find(x => x.num === num); if (!c) return;
  const arr = (field === 'type') ? _CR_TYPE : (field === 'impact') ? _CR_IMPACT : _CR_STATUS;
  const order = arr.map(x => x[0]);
  let idx = order.indexOf(c[field]); if (idx < 0) idx = 0;
  c[field] = order[(idx + 1) % order.length];
  markDirty(); renderChangeRequests();
}
function removeChangeRequest(num) {
  const c = appMeta.changeRequests.find(x => x.num === num);
  if (!c) return;
  const snippet = c.description
    ? _escapeHtml(c.description.length > 60 ? c.description.slice(0, 60) + '\u2026' : c.description)
    : '(no description)';
  showConfirm('Delete ' + _crPad(c.num) + '?',
    'This change request will be removed:<br><br><em>"' + snippet + '"</em><br><br>You\'ll have 6 seconds to undo.',
    () => {
      const arr = appMeta.changeRequests;
      const idx = arr.findIndex(x => x.num === num);
      if (idx < 0) return;
      const removed = arr.splice(idx, 1)[0]; const removedIndex = idx;
      markDirty(); renderChangeRequests();
      showUndoToast('Change request deleted', () => {
        appMeta.changeRequests.splice(removedIndex, 0, removed);
        markDirty(); renderChangeRequests();
        showToast('Change request restored', { type: 'info' });
      });
    });
}

